import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import FeaturedHero from "@/components/FeaturedHero";
import { ArrowRight, Instagram } from "lucide-react";

const CAT_CARDS = [
  { slug: "sarees", label: "Sarees", img: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/6uttp3y2_IMG_2513%202.PNG", pos: "center 22%" },
  { slug: "lehengas", label: "Party Wear", img: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/mwluwmvg_PHOTO-2026-04-25-00-47-54.jpg", pos: "center 30%" },
  { slug: "kurtis", label: "Kurtis", img: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/mfmu6hrr_PHOTO-2026-04-25-00-32-23%202.jpg", pos: "center 30%" },
  { slug: "dresses", label: "Dresses", img: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/fq6ifgsc_PHOTO-2026-04-25-00-32-21%202.jpg", pos: "center 25%" },
];

export default function Home() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [instagramUrl, setInstagramUrl] = useState("https://www.instagram.com/riddhi_drape_the_elegance/");

  useEffect(() => {
    api.get("/settings/site").then(({ data }) => data.instagram_url && setInstagramUrl(data.instagram_url)).catch(() => {});
    api.get("/products", { params: { tag: "new", limit: 8 } }).then(({ data }) => setNewArrivals(data));
    api.get("/products", { params: { tag: "bestseller", limit: 8 } }).then(({ data }) => setBestsellers(data));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FDFBF7]" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          <div className="lg:col-span-5 flex items-center px-6 md:px-12 lg:px-16 py-20 relative z-10">
            <div className="fade-up">
              <div className="text-[11px] tracking-overline uppercase text-[#5A1618] mb-6">Spring / Summer Edit</div>
              <h1 className="font-serif-h font-light text-stone-900 text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
                Draping the World<br /><em className="italic text-[#5A1618]">in Elegance</em>.
              </h1>
              <p className="mt-8 text-stone-700 font-light text-base md:text-lg max-w-lg leading-relaxed">
                Hand-loomed sarees, ivory lehengas, and modern silhouettes — crafted in small batches, worn for lifetimes.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/shop/sarees" className="btn-ink" data-testid="hero-cta-shop">
                  Explore Collection <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                <Link to="/about" className="btn-gold-outline" data-testid="hero-cta-story">Our Story</Link>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative min-h-[520px] lg:min-h-[88vh] bg-[#F8EDE9] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/6f3vtsnt_db7fa853-27e4-4a22-b891-8e48db2e47da.jpg"
              alt="Riddhi — Draping the World in Elegance"
              className="absolute inset-0 w-full h-full object-contain object-center ken-burns"
            />
            <div className="absolute top-0 left-0 h-full w-16 bg-gradient-to-r from-[#FDFBF7] to-transparent pointer-events-none hidden lg:block" />
            <div className="absolute bottom-6 right-6 text-right">
              <div className="font-logo text-xs tracking-widest text-stone-700">EST. 2024</div>
              <div className="h-10 w-px bg-[#D4AF37] mt-2 ml-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="py-6 border-y border-warm bg-[#5A1618]">
        <div className="flex flex-wrap justify-center gap-8 md:gap-16 px-6 text-[11px] tracking-overline uppercase text-[#E9C97A]">
          <span>Complimentary Shipping Above ₹5,000</span>
          <span className="hidden md:inline text-[#D4AF37]">◆</span>
          <span>Handcrafted in India</span>
          <span className="hidden md:inline text-[#D4AF37]">◆</span>
          <span>Easy 7-Day Returns</span>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">The Collections</div>
            <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900 max-w-xl leading-tight">
              Silhouettes that hold tradition and tomorrow.
            </h2>
          </div>
          <Link to="/shop/sarees" className="text-[11px] tracking-overline uppercase text-[#D4AF37] hover:underline">View All →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6" data-testid="categories-grid">
          {CAT_CARDS.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              data-testid={`cat-card-${c.slug}`}
              className={`relative group hover-zoom card-soft overflow-hidden ${i === 0 ? "md:col-span-7 md:row-span-2 h-[520px] md:h-[680px]" : "md:col-span-5 h-[320px]"}`}
            >
              <img src={c.img} alt={c.label} className="w-full h-full object-cover" style={{ objectPosition: c.pos || "center" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <div className="text-[10px] tracking-overline uppercase text-[#D4AF37]">Shop</div>
                <div className="font-serif-h text-3xl md:text-4xl text-white mt-1">{c.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SITE-WIDE FEATURED HERO */}
      <FeaturedHero />

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-10 md:py-16" data-testid="new-arrivals-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Just Arrived</div>
              <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900">New Arrivals</h2>
            </div>
            <Link to="/new" className="text-[11px] tracking-overline uppercase text-[#D4AF37]">See all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {newArrivals.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* EDITORIAL BAND */}
      <section className="my-24 md:my-32 relative grain bg-[#F3EAE8]">
        <div className="px-6 md:px-12 lg:px-24 py-24 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src="https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/36r46ypx_PHOTO-2026-04-25-01-24-01.jpg"
                 alt="Meena & Naveen Raghu — Founders of Riddhi"
                 className="w-full h-[520px] object-cover object-center" />
            <div className="absolute -bottom-8 -right-4 md:-right-10 bg-[#FDFBF7] p-6 md:p-8 border border-warm max-w-[260px] hidden md:block">
              <div className="text-[10px] tracking-overline uppercase text-stone-500">Since 2024</div>
              <div className="font-serif-h text-2xl mt-2">A slow atelier for fast moments.</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4">The Atelier</div>
            <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900 leading-tight">
              Fabric that carries memory.
            </h2>
            <p className="mt-6 text-stone-700 font-light leading-relaxed">
              Every Riddhi piece begins at a handloom, a dye-pot, or a needlepoint in a quiet courtyard. We work with master
              artisans across Benares, Kanchipuram, and Jaipur to bring you drapes that feel heirloom from day one —
              and softer every season.
            </p>
            <Link to="/about" className="btn-gold-outline mt-10">Read Our Story</Link>
          </div>
        </div>
      </section>

      {/* BRIDAL TEASER */}
      <section className="px-6 md:px-12 lg:px-24 pb-24" data-testid="bridal-teaser">
        <Link to="/bridal" className="block group relative overflow-hidden rounded-sm bg-[#0e0b0a]">
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[440px] md:min-h-[520px]">
            <div className="relative h-[320px] md:h-auto bg-[#0e0b0a] order-2 md:order-1">
              <img src="https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/vlw7o2zy_7769cdbb-7aa1-4458-aee9-a7bdd155cacf.jpg"
                   alt="Bridal Collection"
                   className="absolute inset-0 w-full h-full object-contain object-center" />
            </div>
            <div className="relative flex items-center px-8 md:px-14 py-14 order-1 md:order-2 bg-[#1C1917]">
              <div>
                <div className="text-[11px] tracking-overline uppercase text-[#E9C97A] mb-4">New Edit</div>
                <h2 className="font-serif-h font-light text-4xl md:text-6xl text-[#FDFBF7] leading-[0.95]">
                  Bridal<br /><em className="italic text-[#E9C97A]">Collections</em>
                </h2>
                <p className="mt-6 text-stone-200 font-light max-w-md leading-relaxed">
                  Heirloom Kanjeevarams, temple-border silks, and regal lehengas — for the muhurtha worth remembering.
                </p>
                <div className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-overline uppercase text-[#E9C97A] group-hover:text-[#FDFBF7] transition">
                  Explore the Edit <span className="transition-transform group-hover:translate-x-1">→</span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* INSTAGRAM REEL */}
      <section className="px-6 md:px-12 lg:px-24 pb-24" data-testid="instagram-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-[#5A1618] mb-3">@Riddhi · Reels</div>
            <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900 leading-tight">
              Watch us style it.
            </h2>
            <p className="mt-6 text-stone-700 font-light leading-relaxed">
              Peek inside the atelier — fittings, drape tutorials, and the drop-of-the-week, in motion.
            </p>
            <a href={instagramUrl} target="_blank" rel="noreferrer"
               className="btn-maroon mt-8 inline-flex items-center gap-2" data-testid="instagram-cta">
              <Instagram size={16} strokeWidth={1.4} /> Follow us on Instagram
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="mx-auto max-w-[420px] bg-white border border-warm rounded-sm overflow-hidden shadow-sm">
              <iframe
                src="https://www.instagram.com/reel/DW6ZUejjZkw/embed"
                title="Riddhi reel"
                className="w-full"
                style={{ height: "600px", border: 0 }}
                allow="encrypted-media"
                scrolling="no"
                data-testid="instagram-embed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      {bestsellers.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-24" data-testid="bestsellers-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Forever Loved</div>
              <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900">Bestsellers</h2>
            </div>
            <Link to="/bestsellers" className="text-[11px] tracking-overline uppercase text-[#D4AF37]">See all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {bestsellers.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
